import { useRef, type ReactNode } from 'react';
import { MagnifyingGlassIcon, MapPinIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { useMediaQuery } from '@voll-intelligence/ui-kit/hooks/use-media-query';
import { NARROW_STAGE_QUERY } from '../../shared/narrow_stage.js';
import { useGuidedFlow, useGuidedOpen, type FieldGuide } from '../../shared/guided_flow.js';
import { guidedOpensOnMount, type GuidedOpen } from '../../shared/guided_open.js';
import { PlaceField } from '../../shared/place_search/place_field.js';
import type { TravelerFieldSearch } from '../../shared/person/who_search.js';
import type { PlaceSuggestion } from '../../shared/place_search/place_panel.js';
import type { BusSearchPendingField, BusSearchStop, BusSearchValue } from '../models.js';
import { invalidBusSearch, pendingBusSearchFields, withBusDates } from './bus_search_rules.js';
import { BusTravelersField, type BusTravelersCopy } from './bus_travelers_field.js';
import { BusStopSwap, BusTripTabs, missingReturn, type BusTripType } from './bus_search_route.js';
export { type BusTripType } from './bus_search_route.js';
import { BusWhenField, type BusWhenCopy } from './bus_when_field.js';
import './bus_search_bar.css';

/** A busca do QUEM que a barra recebe em `travelerSearch`, pronta para o host. */
export type { BusTravelersCopy } from './bus_travelers_field.js';

/*
 * A barra de busca do ônibus — a entrada determinística do #2440 (a página) e o
 * card da conversa (#5472). A mesma anatomia da busca do aéreo nos dois: as
 * abas "Ida e volta / Somente ida" (`tripType`), o QUEM com o rótulo em cima, e
 * uma fileira com Origem, o inversor, Destino ("cidade de destino"), Quando
 * e o Buscar fechando a fileira à direita (#5593).
 *
 * No palco ESTREITO cada campo troca o popover por uma folha de tela cheia
 * (a do lugar é a do próprio `PlaceField`, a das datas a `PageFieldSheet`).
 *
 * O preenchimento GUIADO (`guided`) é o mesmo do carro e do hotel
 * (`useGuidedFlow`): Origem → Destino → Quando, e confirmar as datas com tudo
 * respondido busca. O QUEM fica fora da fila: nasce com o solicitante assim
 * que o `/me` chega, e o último viajante não sai — uma pendência dele seria a
 * espera do perfil, e não uma pergunta para a pessoa. Só o Buscar o pergunta:
 * travado pelo QUEM vazio, o clique abre o seletor (#5085).
 *
 * O `layout` só muda o Buscar: na página, o botão com o texto, como o do
 * aéreo; na CONVERSA (`inline`, frame 01 do "Conversacional → Bus"), só a
 * lupa. Sem `travelerSearch` o QUEM não existe e a falta de viajante não trava
 * a busca (a conversa: quem viaja é quem conversa). As paradas recentes no
 * campo aberto vazio (`recentStops`) também são as do aéreo.
 */

export type BusSearchCopy = BusWhenCopy & {
  /** A copy do QUEM — só com `travelerSearch`. */
  travelers?: BusTravelersCopy;
  originLabel: string;
  destinationLabel: string;
  /** O vazio de cada ponta ("Cidade de origem" / "Cidade de destino"): no
   *  celular o guia abre uma folha logo depois da outra, e um texto só para as
   *  duas fazia a troca de etapa passar despercebida (#6187). */
  originPlaceholder: string;
  destinationPlaceholder: string;
  /** A linha sob o título da folha do Destino com a origem já escolhida
   *  ("Saindo de Uberlândia, MG") — a confirmação de que ela foi aceita. */
  departingFromLabel: (origin: string) => string;
  noStopsLabel: string;
  stopErrorLabel: string;
  /** Abaixo de dois caracteres a busca de rodoviária não roda. */
  stopPromptLabel: string;
  searchLabel: string;
  /** A seta de voltar das folhas do palco estreito ("Voltar"). */
  backLabel: string;
  /** O inversor entre as paradas ("Inverter origem e destino"). Ausente ⇒ sem
   *  o inversor. */
  swapLabel?: string;
  /** As abas da forma da viagem — só com `tripType`. */
  tripTypeLabels?: { roundTrip: string; oneWay: string };
};

export type BusSearchBarProps = {
  value: BusSearchValue;
  copy: BusSearchCopy;
  /** O QUEM. Ausente ⇒ sem o QUEM, e a busca não espera viajante. */
  travelerSearch?: TravelerFieldSearch;
  /** `page` (padrão): o Buscar com o texto, como o do aéreo. `inline`: só a
   *  lupa, como o card da conversa. Nos dois ele fecha a fileira. */
  layout?: 'page' | 'inline';
  /** Rodoviárias para o termo — `id` é o slug do bus-api, `label` o nome. */
  searchStops(term: string): Promise<readonly PlaceSuggestion[]>;
  /** Hoje, no fuso da pessoa (`YYYY-MM-DD`): o piso do calendário e da ida. */
  today: string;
  /** Uma busca em voo: os campos e o Buscar travam. */
  busy?: boolean;
  locale?: string;
  onChange(value: BusSearchValue): void;
  /** A busca, com o valor que ela leva — do Buscar ou do Confirmar das datas
   *  no preenchimento guiado, que sai antes do commit do `onChange`. */
  onSubmit(value: BusSearchValue): void;
  /** O preenchimento guiado da página (ver o cabeçalho). Ausente ⇒ nada disso. */
  guided?: boolean;
  /** Em que palco o guia abre o primeiro pendente ao nascer (`GuidedOpen`). */
  guidedOpen?: GuidedOpen;
  /** O que a pessoa já disse de cada parada sem que ela tenha sido resolvida
   *  ("Brusque"): o campo vazio mostra o termo e abre buscando por ele. */
  stopDrafts?: { from?: string; to?: string };
  /** A forma da viagem, com as abas no topo. Ausente ⇒ sem abas: a volta é
   *  opcional no próprio calendário (a página). */
  tripType?: BusTripType;
  onTripTypeChange?(tripType: BusTripType): void;
  /** As paradas que a pessoa já buscou, por ponta: o campo aberto vazio as
   *  lista em vez de pedir o termo. */
  recentStops?: { from?: readonly PlaceSuggestion[]; to?: readonly PlaceSuggestion[] };
};

type FieldSheet = { readonly backLabel: string } | undefined;

const draftOf = (drafts: BusSearchBarProps['stopDrafts'], field: 'from' | 'to') => drafts?.[field];

/** Uma coluna da fileira: o rótulo da pergunta em cima, o campo embaixo. O
 *  rótulo é `aria-hidden`: o nome acessível já sai do próprio campo. */
function BusFieldColumn({
  label,
  field,
  children,
}: {
  readonly label: string;
  readonly field: 'stop' | 'when';
  readonly children: ReactNode;
}): ReactNode {
  return (
    <div className="iuk-bus-search-field" data-field={field}>
      <span aria-hidden="true" className="iuk-bus-search-label">
        {label}
      </span>
      <div className="relative min-w-0">{children}</div>
    </div>
  );
}

function StopColumn({
  label,
  value,
  copy,
  sheet,
  disabled,
  searchStops,
  guide,
  draft,
  recent,
  side,
  origin,
  onPick,
}: {
  readonly label: string;
  readonly value: BusSearchStop | undefined;
  readonly copy: BusSearchCopy;
  readonly sheet: FieldSheet;
  readonly disabled: boolean;
  readonly searchStops: BusSearchBarProps['searchStops'];
  readonly guide: FieldGuide;
  readonly draft: string | undefined;
  readonly recent: BusSearchBarProps['recentStops'];
  readonly side: 'from' | 'to';
  /** A origem escolhida — só o Destino a mostra na folha. */
  readonly origin?: BusSearchStop;
  readonly onPick: (stop: BusSearchStop) => void;
}): ReactNode {
  // Controlada para fechar quando a parada chega de fora (a rodoviária das
  // Preferências preenche a Origem depois que ela já nasceu aberta).
  const [open, setOpen] = useGuidedOpen(guide, value?.code);
  const recentHere = recent?.[side];
  const subtitle = side === 'to' && origin ? copy.departingFromLabel(origin.name) : undefined;
  return (
    <BusFieldColumn label={label} field="stop">
      <PlaceField<PlaceSuggestion>
        label={label}
        {...(value ? { value: { title: value.name }, selectedPlaceId: value.code } : {})}
        emptyText={side === 'from' ? copy.originPlaceholder : copy.destinationPlaceholder}
        {...(draft ? { draft } : {})}
        icon={<MapPinIcon className="size-3.5" aria-hidden="true" />}
        variant="outline"
        disabled={disabled}
        copy={{
          label,
          emptyLabel: copy.noStopsLabel,
          errorLabel: copy.stopErrorLabel,
          promptLabel: copy.stopPromptLabel,
        }}
        minChars={2}
        {...(recentHere && recentHere.length > 0 ? { zeroQuery: recentHere } : {})}
        onSearch={searchStops}
        {...(sheet ? { sheet: subtitle ? { ...sheet, subtitle } : sheet } : {})}
        wanted={guide.wanted}
        open={open}
        // Aberto pelo guia (continuação do pick anterior): o input nasce focado.
        openedByGesture={guide.advance}
        onOpenChange={setOpen}
        onPick={(place) => {
          // Sob abertura controlada o `PlaceField` não fecha sozinho no pick.
          setOpen(false);
          onPick({ code: place.id, name: place.label });
        }}
      />
    </BusFieldColumn>
  );
}

/** A fila da barra sem o QUEM: as paradas e as datas da viagem — o que o guia
 *  pergunta e o que a conversa (quem viaja é quem conversa) espera. */
function tripFields(value: BusSearchValue): readonly BusSearchPendingField[] {
  return pendingBusSearchFields(value).filter((field) => field !== 'travelers');
}

/** O Buscar no fim da fileira: com o texto na página, só a lupa na conversa. */
function SearchButton({
  layout,
  label,
  disabled,
  buttonRef,
  onClick,
}: {
  readonly layout: 'page' | 'inline';
  readonly label: string;
  readonly disabled: boolean;
  readonly buttonRef: React.RefObject<HTMLButtonElement | null>;
  readonly onClick: () => void;
}): ReactNode {
  if (layout === 'inline') {
    return (
      <Button
        ref={buttonRef}
        // A lupa redonda do card de voo do chat (`InlineSearchCta` com
        // `iconOnly`): a mesma régua e o mesmo raio.
        size="icon-lg"
        className="shrink-0 self-end rounded-full"
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
      >
        <MagnifyingGlassIcon />
      </Button>
    );
  }
  return (
    <Button
      ref={buttonRef}
      size="lg"
      className="iuk-bus-search-submit shrink-0 rounded-full px-4"
      disabled={disabled}
      onClick={onClick}
    >
      <MagnifyingGlassIcon />
      {label}
    </Button>
  );
}

const isWhen = (field: BusSearchPendingField): boolean => field === 'outwardDate';

/** O QUEM sob as abas, com o rótulo em cima como os outros campos. Sem a
 *  busca de viajantes, nada. */
function BusSearchWho({
  value,
  search,
  copy,
  disabled,
  guide,
  locale,
  onChange,
}: {
  readonly value: BusSearchValue;
  readonly search: TravelerFieldSearch | undefined;
  readonly copy: BusTravelersCopy | undefined;
  readonly disabled: boolean;
  readonly guide: FieldGuide;
  readonly locale: string | undefined;
  readonly onChange: (value: BusSearchValue) => void;
}): ReactNode {
  if (!search || !copy) return null;
  return (
    <div className="iuk-bus-search-head">
      <span aria-hidden="true" className="iuk-bus-search-label">
        {copy.label}
      </span>
      <BusTravelersField
        travelers={value.travelers}
        {...(value.policyTravelerId ? { policyTravelerId: value.policyTravelerId } : {})}
        search={search}
        copy={copy}
        disabled={disabled}
        guide={guide}
        {...(locale ? { locale } : {})}
        onChange={(next) => {
          onChange({ ...value, ...next });
        }}
      />
    </div>
  );
}

export function BusSearchBar({
  value,
  copy,
  travelerSearch,
  layout = 'page',
  searchStops,
  today,
  busy = false,
  locale,
  guided = false,
  guidedOpen,
  stopDrafts,
  tripType,
  onTripTypeChange,
  recentStops,
  onChange,
  onSubmit,
}: Readonly<BusSearchBarProps>): ReactNode {
  const narrow = useMediaQuery(NARROW_STAGE_QUERY);
  const sheet: FieldSheet = narrow ? { backLabel: copy.backLabel } : undefined;
  // Sem o QUEM (a conversa), quem viaja é quem conversa: não há o que esperar.
  const pending = (next: BusSearchValue) =>
    travelerSearch ? pendingBusSearchFields(next) : tripFields(next);
  const blockedFor = (next: BusSearchValue): boolean =>
    busy ||
    pending(next).length > 0 ||
    missingReturn(tripType, next) ||
    invalidBusSearch(next, today);
  const searchRef = useRef<HTMLButtonElement>(null);
  const guide = useGuidedFlow({
    guided: guided && !busy,
    value,
    pending: tripFields,
    opensOnMount: guidedOpensOnMount(guidedOpen, narrow),
    submitRef: searchRef,
    // Só o que as datas por confirmar não corrigem: as paradas (a mesma nas
    // duas pontas). Sem as datas, `invalidBusSearch` olha só para elas.
    vetoed: invalidBusSearch(withBusDates(value, {}), today),
    canSubmit: (next) => !blockedFor(next),
    onSubmit,
  });
  const stopGuide = (field: 'from' | 'to'): FieldGuide =>
    guide.fieldGuide((pending) => pending === field);
  const pickStop = (next: BusSearchValue) => {
    onChange(next);
    guide.answer(next);
  };
  const search = (
    <SearchButton
      layout={layout}
      label={copy.searchLabel}
      buttonRef={searchRef}
      // A fila do Buscar inclui o QUEM da página (`pending`), que o guia não
      // pergunta sozinho. Um pedido inválido (data vencida ou fora de ordem)
      // trava mesmo com campo vazio: responder o campo não o corrigiria.
      {...guide.searchCta(
        blockedFor(value),
        () => {
          onSubmit(value);
        },
        invalidBusSearch(value, today) ? [] : pending(value),
      )}
    />
  );
  const who = {
    value,
    search: travelerSearch,
    copy: copy.travelers,
    disabled: busy,
    guide: guide.fieldGuide((field) => field === 'travelers'),
    locale,
    onChange,
  };
  return (
    <div className="iuk-bus-search" data-layout={layout}>
      <BusTripTabs
        tripType={tripType}
        labels={copy.tripTypeLabels}
        disabled={busy}
        value={value}
        onChange={onChange}
        onTripTypeChange={onTripTypeChange}
      />
      <BusSearchWho {...who} />
      <div className="iuk-bus-search-row">
        <StopColumn
          label={copy.originLabel}
          value={value.from}
          copy={copy}
          sheet={sheet}
          disabled={busy}
          searchStops={searchStops}
          guide={stopGuide('from')}
          draft={draftOf(stopDrafts, 'from')}
          recent={recentStops}
          side="from"
          onPick={(from) => {
            pickStop({ ...value, from });
          }}
        />
        <BusStopSwap label={copy.swapLabel} disabled={busy} value={value} onChange={onChange} />
        <StopColumn
          label={copy.destinationLabel}
          value={value.to}
          copy={copy}
          sheet={sheet}
          disabled={busy}
          searchStops={searchStops}
          guide={stopGuide('to')}
          draft={draftOf(stopDrafts, 'to')}
          recent={recentStops}
          side="to"
          origin={value.from}
          onPick={(to) => {
            pickStop({ ...value, to });
          }}
        />
        <BusFieldColumn label={copy.whenLabel} field="when">
          <BusWhenField
            value={{
              ...(value.outwardDate ? { from: value.outwardDate } : {}),
              ...(value.returnDate ? { to: value.returnDate } : {}),
            }}
            copy={copy}
            minDate={today}
            disabled={busy}
            sheet={sheet}
            guide={guide.fieldGuide(isWhen)}
            searches={guide.confirmSearches(isWhen)}
            oneWay={tripType === 'one_way'}
            needsReturn={tripType === 'round_trip'}
            onConfirm={(range) => {
              const next = withBusDates(value, range);
              onChange(next);
              guide.confirm(next);
            }}
          />
        </BusFieldColumn>
        {search}
      </div>
    </div>
  );
}
